import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  FormHelperText,
  Tooltip,
  Typography,
} from "@mui/joy";
import { useMediaQuery } from "@mui/material";

interface FormFieldProps {
  name: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  onBlur: (e: React.FocusEvent<any>) => void;
  touched?: boolean;
  error?: string;
  type?: string;
  autoComplete?: string;
  infoText?: string;
  infoIcon?: string;
  endDecorator?: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  touched,
  error,
  type = "text",
  autoComplete,
  infoText,
  infoIcon,
  endDecorator,
}) => {
  const isMobile = useMediaQuery("(max-width:500px)");
  const position = isMobile ? "top-start" : "right";
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const iconRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        iconRef.current &&
        !(iconRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setTooltipOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <FormControl
      error={!!(touched && error)}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "start",
          mb: {
            xs: "8px",
            lg: "12px",
          },
        }}
      >
        <FormLabel
          htmlFor={name}
          sx={{
            fontSize: {
              xs: "16px",
              lg: "18px",
            },
            fontWeight: 500,
            color: "#1A1B1D",
          }}
        >
          {label}
        </FormLabel>

        {infoText && infoIcon && (
          <Tooltip
            open={tooltipOpen}
            title={
              <Typography
                sx={{
                  fontSize: {
                    xs: "13px",
                    lg: "16px",
                  },
                  fontWeight: "normal",
                  color: "#ffffff",
                }}
              >
                {infoText}
              </Typography>
            }
            placement={position}
            arrow
            variant="solid"
            sx={{
              backgroundColor: "#1A1B1D",
              maxWidth: { xs: "270px", sm: "300px" },
              "& .MuiTooltip-arrow": {
                color: "#1A1B1D",
                "&::before": {
                  borderTopColor: "#1A1B1D",
                  borderBottomColor: "#1A1B1D",
                  borderLeftColor: "#1A1B1D",
                  borderRightColor: "#1A1B1D",
                },
              },
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#1A1B1D",
                color: "#ffffff",
                fontSize: "14px",
                fontWeight: 300,
                lineHeight: "1.25",
                padding: "8px 12px",
              },
            }}
          >
            <Box
              component="img"
              src={infoIcon}
              alt="info"
              ref={iconRef}
              onClick={(e) => {
                e.stopPropagation();
                setTooltipOpen((prev) => !prev);
              }}
              sx={{
                ml: "8px",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Input
        id={name}
        type={type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        autoComplete={autoComplete}
        variant="plain"
        endDecorator={endDecorator}
        sx={{
          height: "52px",
          width: "100%",
          marginBottom: "8px",
          borderRadius: "12px",
          bgcolor: "#edeff3",
          border: "none",
          outline: "none",
          fontSize: "16px"
        }}
      />
      {touched && error && (
        <FormHelperText
          sx={{
            color: "#ef4444",
            fontSize: "14px",
            mt: "4px",
          }}
        >
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default FormField;
